/** Strip HTML tags and decode common entities (handles double-encoded RSS content) */
export function stripHtml(html: string): string {
  let text = html;
  // First pass: decode &amp; so double-encoded entities like &amp;lt; become &lt;
  text = text.replace(/&amp;/g, "&");
  // Strip HTML tags (run twice to catch tags revealed by first decode)
  text = text.replace(/<[^>]*>/g, "");
  text = text.replace(/<[^>]*>/g, "");
  // Decode named entities
  text = text.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"');
  text = text.replace(/&amp;/g, "&");
  text = text.replace(/&#39;/g, "'").replace(/&#x27;/gi, "'");
  text = text.replace(/&nbsp;/g, " ");
  text = text.replace(/&rsquo;/g, "\u2019").replace(/&lsquo;/g, "\u2018");
  text = text.replace(/&rdquo;/g, "\u201C").replace(/&ldquo;/g, "\u201D");
  text = text.replace(/&mdash;/g, "\u2014").replace(/&ndash;/g, "\u2013");
  text = text.replace(/&hellip;/g, "\u2026");
  text = text.replace(/&trade;/g, "\u2122").replace(/&copy;/g, "\u00A9").replace(/&reg;/g, "\u00AE");
  // Decode numeric (&#123;) and hex (&#x1F;) entities
  text = text.replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)));
  text = text.replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec)));
  // Collapse excessive whitespace but preserve single newlines
  text = text.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n");
  return text.trim();
}

export function formatCount(count: number | null | undefined): string {
  if (count == null || isNaN(count)) return "0";
  if (count < 1000) return count.toString();
  if (count < 1000000) return (count / 1000).toFixed(1) + "K";
  if (count < 1000000000) return (count / 1000000).toFixed(1) + "M";
  return (count / 1000000000).toFixed(1) + "B";
}

export function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "";
  // Ensure timestamps without timezone info are treated as UTC
  let normalized = dateStr;
  if (!dateStr.includes("Z") && !dateStr.includes("+") && !/[-+]\d{2}:\d{2}$/.test(dateStr)) {
    normalized = dateStr.replace(" ", "T") + "Z";
  }
  const seconds = Math.floor(
    (Date.now() - new Date(normalized).getTime()) / 1000,
  );

  if (seconds < 0) return "now";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const date = new Date(normalized);
  const currentYear = new Date().getFullYear();
  const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (date.getFullYear() !== currentYear) {
    options.year = "numeric";
  }
  return date.toLocaleDateString("en-US", options);
}

export function extractHashtags(text: string): string[] {
  const hashtags = text.match(/#\w+/g);
  return hashtags ? hashtags.map((tag) => tag.slice(1).toLowerCase()) : [];
}

export const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/g;

export function normalizeRemoteMediaUrl(url: string | null | undefined): string | null {
  const trimmed = typeof url === "string" ? url.trim() : "";
  if (!trimmed) return null;
  if (/^http:\/\//i.test(trimmed)) return `https://${trimmed.slice(7)}`;
  return trimmed;
}

export function extractUrls(content: string): string[] {
  return content.match(URL_REGEX) || [];
}

export function stripUrls(content: string, urls: string[]): string {
  let result = content;
  for (const url of urls) result = result.replaceAll(url, "");
  return result.replace(/\n{3,}/g, "\n\n").trim();
}

export type ContentSegment = string | { mention: string } | { hashtag: string };

/**
 * Parse content into text and mention segments for rendering.
 * "Hey @alice check" -> ["Hey ", { mention: "alice" }, " check"]
 */
export function parseMentionSegments(content: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  const regex = /@(\w+)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push(content.slice(lastIndex, match.index));
    }
    segments.push({ mention: match[1] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) {
    segments.push(content.slice(lastIndex));
  }
  return segments;
}

/**
 * Parse content into text, mention, and hashtag segments for rendering.
 * "Hey @alice check #music" -> ["Hey ", { mention: "alice" }, " check ", { hashtag: "music" }]
 */
export function parseContentSegments(content: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  const regex = /(@(\w+))|(#(\w+))/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push(content.slice(lastIndex, match.index));
    }
    if (match[2]) {
      segments.push({ mention: match[2] });
    } else if (match[4]) {
      segments.push({ hashtag: match[4] });
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) {
    segments.push(content.slice(lastIndex));
  }
  return segments;
}

/**
 * Extract unique @usernames from content (max count).
 */
export function extractMentionUsernames(content: string, max = 5): string[] {
  const matches = content.match(/@(\w+)/g);
  if (!matches) return [];
  const unique = Array.from(new Set(matches.map((m) => m.slice(1).toLowerCase())));
  return unique.slice(0, max);
}

/** Critical auth query options that prevent the Clerk token refresh infinite loop */
export const authQueryOptions = {
  retry: false,
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
};

export function getYouTubeVideoId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.match(/^(www\.)?(youtube\.com|youtu\.be|m\.youtube\.com)$/)) {
      if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split(/[?/]/)[0] || null;
      return u.searchParams.get("v");
    }
  } catch {}
  return null;
}

export function getYouTubeThumbnailUrl(videoId: string, quality: "hqdefault" | "maxresdefault" = "hqdefault"): string {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

export {
  buildDraftRestoreState,
  buildComposePoll,
  buildEpisodeDraft,
  classifyDraft,
  routeForDraft,
  buildComposePostBody,
  buildOptimisticPostItem,
  buildPostDraft,
  buildVideoUploadMetadata,
  formatRelativeTime,
  getMaxUploadVideoDurationSeconds,
  getDraftPollState,
  hasDraftContent,
  normalizeReplyPermission,
  REPLY_PERMISSION_VALUES,
  shouldShowVideoUploadUpsell,
  VIDEO_UPLOAD_UPSELL_DISMISSED_KEY,
} from "./compose";
export type {
  ComposeImageFieldMode,
  ComposePoll,
  DraftKind,
  EpisodeDraftData,
  PostDraft,
  ReplyPermission,
} from "./compose";

export {
  ACTIVE_PUBLISH_PRERENDER_STATUSES,
  PREPUBLISH_RENDER_PIPELINE_VERSION,
  PUBLISH_PRERENDER_WATERMARK_IDENTITY,
  buildPublishPrerenderKey,
  isActivePublishPrerenderStatus,
  stableHash,
  stableStringify,
} from "./publish-prerender";
export type {
  ActivePublishPrerenderStatus,
  PublishPrerenderKeyInput,
  PublishPrerenderStatus,
} from "./publish-prerender";

export {
  STREAK_FULL,
  STREAK_MILESTONES,
  STREAK_GRADIENT_PALETTE_LIGHT,
  STREAK_GRADIENT_PALETTE_DARK,
  getStreakMessage,
  getStreakStatus,
  getLocalDateStr,
  getStreakGradientStops,
  getNextMilestone,
  getAtRiskAccent,
  resolveAccent,
} from "./streaks";
export type {
  StreakMilestone,
  StreakStatus,
  StreakGradientStop,
  StreakGradientPalette,
} from "./streaks";

export {
  FEATURE_CATALOG,
  getFeatureMetadata,
  getFeatureUsageForPath,
  normalizeAnalyticsPath,
  sanitizeAnalyticsPath,
} from "./feature-analytics";
export type {
  FeatureKey,
  FeatureUsageInfo,
} from "./feature-analytics";

export {
  PODCAST_CHAPTERS_JSON_VERSION,
  MAX_CHAPTERS_PER_EPISODE,
  MAX_CHAPTER_TITLE_LENGTH,
  MAX_CHAPTER_URL_LENGTH,
  MAX_CHAPTER_IMAGE_URL_LENGTH,
  parseChapterTimestamp,
  formatChapterTimestamp,
  normalizeScrollChapters,
  normalizePodcastChaptersJson,
  extractChapterTagFromItemXml,
  normalizeEpisodeChapters,
  getCurrentChapter,
  getNextChapter,
  getPreviousChapter,
  toPodcastChaptersJson,
  isUnsafeChapterUrl,
} from "./podcast-chapters";
export type {
  EpisodeChapter,
  EpisodeChapterSource,
  PodcastChaptersJsonChapter,
} from "./podcast-chapters";

export {
  classifyPodcastVideoMatch,
  comparePodcastVideoSources,
  durationClosenessScore,
  extractLikelyGuestEntities,
  extractPodcastVideoAlternatesFromItemXml,
  guestEntityOverlap,
  hasNegativePodcastVideoLabel,
  normalizePodcastVideoTitle,
  passesFullEpisodeDurationGate,
  publishedDateProximityScore,
  scorePodcastVideoMatch,
  selectPrimaryPodcastVideoAlternate,
  selectPrimaryPodcastVideoSource,
  attachPodcastEpisodeVideoSources,
  isEligiblePodcastEpisodeVideoSource,
  serializePodcastEpisodeVideoSource,
  titleSimilarity,
} from "./podcast-video-matching";
export type {
  PodcastEpisodeVideoSource,
  PodcastEpisodeVideoSourceRecord,
  PodcastEpisodeVideoSourceRow,
  PodcastVideoAlternate,
  PodcastVideoCatalogRecord,
  PublicPodcastEpisodeVideoSourceRecord,
  PodcastVideoGateBreakdown,
  PodcastVideoMatchMethod,
  PodcastVideoMatchStatus,
  PodcastVideoProvider,
  PodcastVideoScoreBreakdown,
  PodcastVideoScoreInput,
  PodcastVideoScoreResult,
  PodcastVideoSourceCandidate,
} from "./podcast-video-matching";

export {
  extractYoutubeVideoIdsFromText,
  normalizePodcastYoutubeSourceInput,
  parseYoutubeIsoDurationSeconds,
  youtubeChannelLookupFromSourceUrl,
} from "./podcast-video-youtube";
export type {
  NormalizedPodcastYoutubeSource,
  PodcastYoutubeChannelLookup,
  PodcastYoutubeSourceInput,
  PodcastYoutubeSourceKind,
} from "./podcast-video-youtube";

export {
  MIN_CLIP_MS,
  MAX_CLIP_MS,
  MAX_WORDS_PER_LINE,
  MAX_CHARS_PER_LINE,
  MAX_CAPTION_LINES,
  MAX_CAPTION_LINE_CHARS,
  MAX_WORDS_PER_VALIDATED_LINE,
  MAX_WORD_CHARS,
  clampClipRange,
  snapClipStart,
  sliceTranscriptToClip,
  validateClipCaptions,
  captionsModerationText,
} from "./podcast-clips";
export type {
  ClipRange,
  TranscriptWord,
  EpisodeTranscript,
  CaptionLine,
  ClipCaptions,
} from "./podcast-clips";

export * from "./badge-geometry";

export { buildCompatibilityFeedUrls } from "./podcast-feed-aliases";
export type {
  PodcastFeedAliasRow,
  PodcastFeedAliasesResponse,
  CompatibilityFeedUrl,
} from "./podcast-feed-aliases";
